import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Job, JobStatus } from '@core/generated/gql-types';
import PercentageDifference from './PercentageDifference';

interface HoursCompletedProps {
  filledJobs: Job[];
  priorWeekFilledJobs: Job[];
}

const HoursCompleted = ({
  filledJobs,
  priorWeekFilledJobs,
}: HoursCompletedProps) => {
  const getWorkedHours = (jobs: Job[]): number => {
    return jobs.reduce((totalHours, job) => {
      if (!job.startDate || !job.endDate) return totalHours;

      if (job.itemStatus === JobStatus.Completed) {
        const jobStart = job.clockInTime
          ? dayjs(job.clockInTime)
          : dayjs(job.startDate);
        const jobEnd = job.clockOutTime
          ? dayjs(job.clockOutTime)
          : dayjs(job.endDate);

        const jobDurationMinutes =
          jobEnd.diff(jobStart, 'minute', true) -
          (job?.unpaidBreakDurationMinutes || 0);
        totalHours += jobDurationMinutes / 60; // Convert minutes to hours.
      }

      return totalHours;
    }, 0);
  };

  const manHoursCompleted = useMemo(
    (): number => getWorkedHours(filledJobs),
    [filledJobs],
  );

  const priorWeekManHoursCompleted = useMemo(
    (): number => getWorkedHours(priorWeekFilledJobs),
    [priorWeekFilledJobs],
  );

  return (
    <div className="flex h-auto min-h-[140px] flex-row items-center rounded-xl bg-white p-4 shadow">
      <div className="flex h-full flex-1 flex-col justify-between">
        <div className="text-md font-semibold">Hours completed</div>
        <div className="mb-2 text-3xl font-medium">
          {manHoursCompleted.toFixed(0).toLocaleString()}
        </div>
        <PercentageDifference
          currentValue={manHoursCompleted}
          priorValue={priorWeekManHoursCompleted}
        />
      </div>
    </div>
  );
};

export default HoursCompleted;
